import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { useSocialStore, displayName } from '@/store/social'
import { guildApi } from '@/lib/guildApi'
import { describeError } from '@/lib/auth'
import { inviteLink } from '@/lib/guildUi'
import { avatarUrl } from '@/lib/avatar'
import type { Guild, Invite } from '@/types/api'
import { Modal } from '../settings/Modal'
import { Avatar } from '../Avatar'
import styles from './InviteModal.module.css'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/** Пригласить людей: ссылка на 7 дней и быстрая отправка друзьям в ЛС. */
export function InviteModal({ guild, channelId, onClose }: { guild: Guild; channelId: string; onClose: () => void }) {
  const [invite, setInvite] = useState<Invite | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [sent, setSent] = useState<Record<string, boolean>>({})
  const relationships = useSocialStore((s) => s.relationships)
  const openDM = useSocialStore((s) => s.openDM)
  const sendMessage = useSocialStore((s) => s.sendMessage)
  const friends = Object.values(relationships).filter((r) => r.type === 1).sort((a, b) => displayName(a.user).localeCompare(displayName(b.user), 'ru'))

  useEffect(() => {
    let cancelled = false
    guildApi
      .createInvite(channelId, { max_age: 604800 })
      .then((inv) => { if (!cancelled) setInvite(inv) })
      .catch((err: unknown) => { if (!cancelled) setError(describeError(err).message) })
    return () => { cancelled = true }
  }, [channelId])

  const link = invite ? inviteLink(invite.code) : ''
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* буфер недоступен — ссылка видна в поле */
    }
  }
  const sendTo = async (userId: string) => {
    try {
      const ch = await openDM(userId)
      await sendMessage(ch, `Приглашаю на сервер «${guild.name}»: ${link}`)
      setSent((s) => ({ ...s, [userId]: true }))
    } catch (err) {
      setError(describeError(err).message)
    }
  }

  return (
    <Modal title={`Пригласить на «${guild.name}»`} onClose={onClose} width={480}>
      {friends.length ? (
        <ul className={styles.friends}>
          {friends.map((r) => (
            <li key={r.id} className={styles.friend}>
              <Avatar initial={displayName(r.user).slice(0, 1).toUpperCase()} color={colorOf(r.id)} size={32} src={avatarUrl(r.user.id, r.user.avatar)} decoration={r.user.avatar_decoration_data} />
              <span className={styles.friendName}>{displayName(r.user)} <span className={styles.friendUsername}>@{r.user.username}</span></span>
              <button type="button" className={styles.send} disabled={!invite || sent[r.id]} onClick={() => void sendTo(r.id)}>{sent[r.id] ? 'Отправлено' : 'Пригласить'}</button>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.hint}>Друзей пока нет — отправьте ссылку любым способом.</p>
      )}
      <div className={styles.linkBox}>
        <span className={styles.linkLabel}>Или отправьте ссылку-приглашение</span>
        <div className={styles.linkRow}>
          <input className={styles.linkInput} readOnly value={link || (error ?? 'Создаём ссылку…')} aria-label="Ссылка-приглашение" onFocus={(e) => e.currentTarget.select()} />
          <button type="button" className={styles.copy} disabled={!invite} onClick={() => void copy()}>
            {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Скопировано' : 'Копировать'}
          </button>
        </div>
        <span className={styles.expires}>Ссылка действует 7 дней.</span>
      </div>
    </Modal>
  )
}
